import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateOrderNotificationEmails } from '~/api/organizations';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 320;

type Errors = { domestic?: boolean; international?: boolean };

type Saved = { domestic?: string | null; international?: string | null } | null;

const validate = (value: string) =>
  value === '' ||
  (EMAIL_PATTERN.test(value) && value.length <= MAX_EMAIL_LENGTH);

export function OrderNotificationsSection({
  saved,
}: Readonly<{ saved: Saved }>) {
  const { t } = useTranslation('settings');

  const [domestic, setDomestic] = useState(saved?.domestic ?? '');
  const [international, setInternational] = useState(
    saved?.international ?? ''
  );
  const [errors, setErrors] = useState<Errors>({});
  const [lastSyncedSaved, setLastSyncedSaved] = useState(saved);

  if (
    saved?.domestic !== lastSyncedSaved?.domestic ||
    saved?.international !== lastSyncedSaved?.international
  ) {
    setLastSyncedSaved(saved);
    setDomestic(saved?.domestic ?? '');
    setInternational(saved?.international ?? '');
  }

  const mutation = useUpdateOrderNotificationEmails();

  const dirty =
    domestic !== (saved?.domestic ?? '') ||
    international !== (saved?.international ?? '');

  const onSave = () => {
    const nextErrors: Errors = {
      domestic: !validate(domestic.trim()),
      international: !validate(international.trim()),
    };
    setErrors(nextErrors);
    if (nextErrors.domestic || nextErrors.international) return;

    mutation.mutate({
      input: {
        domestic: domestic.trim() || null,
        international: international.trim() || null,
      },
    });
  };

  const onCancel = () => {
    setDomestic(saved?.domestic ?? '');
    setInternational(saved?.international ?? '');
    setErrors({});
  };

  const field = (
    id: 'domestic' | 'international',
    value: string,
    onChange: (next: string) => void
  ) => (
    <div className="mb-4">
      <label
        htmlFor={`order-notifications-${id}`}
        className="block text-xs font-semibold text-neut-700 mb-1"
      >
        {t(`settings.order_notifications.${id}_label`)}
      </label>
      <input
        id={`order-notifications-${id}`}
        data-testid={`order-notifications-${id}`}
        type="email"
        value={value}
        maxLength={MAX_EMAIL_LENGTH}
        placeholder={t('settings.order_notifications.placeholder')}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-neut-200 bg-neut-bg-alt px-3 py-2 text-sm text-neut-prim focus:border-prim focus:outline-none"
      />
      <p className="mt-1 text-[11px] text-neut-400">
        {t(`settings.order_notifications.${id}_hint`)}
      </p>
      {errors[id] && (
        <p
          data-testid={`order-notifications-${id}-error`}
          className="mt-1 text-[11px] text-error-600"
        >
          {t('settings.order_notifications.invalid_email')}
        </p>
      )}
    </div>
  );

  return (
    <div className="w-full rounded-xl border border-neut-200 bg-neut-bg-alt p-5">
      <h2 className="text-sm font-semibold text-neut-prim">
        {t('settings.order_notifications.title')}
      </h2>
      <p className="mt-1 text-xs text-neut-sec">
        {t('settings.order_notifications.description')}
      </p>
      <p className="mt-1 mb-5 text-[11px] text-neut-400">
        {t('settings.order_notifications.fallback_note')}
      </p>

      {field('domestic', domestic, setDomestic)}
      {field('international', international, setInternational)}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          data-testid="order-notifications-cancel"
          onClick={onCancel}
          disabled={!dirty || mutation.isPending}
          className="rounded-lg border border-neut-200 px-4 py-2 text-sm font-medium text-neut-sec disabled:opacity-50"
        >
          {t('settings.order_notifications.cancel')}
        </button>
        <button
          type="button"
          data-testid="order-notifications-save"
          onClick={onSave}
          disabled={!dirty || mutation.isPending}
          className="rounded-lg bg-prim px-4 py-2 text-sm font-semibold text-neut-light disabled:opacity-50"
        >
          {t('settings.order_notifications.save')}
        </button>
      </div>

      {mutation.isSuccess && (
        <p
          data-testid="order-notifications-success"
          className="mt-3 text-[11px] text-success-600"
        >
          {t('settings.order_notifications.save_success')}
        </p>
      )}
      {mutation.isError && (
        <p
          data-testid="order-notifications-error"
          className="mt-3 text-[11px] text-error-600"
        >
          {t('settings.order_notifications.save_error')}
        </p>
      )}
    </div>
  );
}
